import { ApolloClient, NormalizedCacheObject, useLazyQuery } from '@apollo/client';
import {
  CanResetPasswordData,
  CanResetPasswordVars,
  RequestLinkedAccountResetPasswordData,
  RequestLinkedAccountResetPasswordVars,
  RequestResetPasswordData,
  RequestResetPasswordVars
} from '../gql-types';

import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import { PublicGraphQL } from '../gql';
import { UserUsername } from '../models/User';

let client: ApolloClient<NormalizedCacheObject> = null;

export const useRequestPasswordReset = () => {
  client = client || ApolloClientService.create(Config.MEMBERSHIP_PUBLIC_GQL);

  const [
    _canResetPassword,
    {
      loading: checkingUser,
      error: canResetPasswordError,
      data: canResetPasswordData
    }
  ] = useLazyQuery<CanResetPasswordData, CanResetPasswordVars>(
    PublicGraphQL.Query.CAN_RESET_PASSWORD,
    {
      client,
      fetchPolicy: 'network-only'
    }
  );

  const canResetPassword = (tenant: string, login: string) =>
    _canResetPassword({
      variables: {
        tenant,
        login
      }
    });

  const [
    _requestResetPassword,
    {
      loading: requestResetPasswordLoading,
      error: requestResetPasswordError,
      data: requestResetPasswordData
    }
  ] = useLazyQuery<RequestResetPasswordData, RequestResetPasswordVars>(
    PublicGraphQL.Query.REQUEST_RESET_PASSWORD,
    {
      client,
      fetchPolicy: 'network-only'
    }
  );

  const requestResetPassword = (
    locationOrigin: string,
    tenant: string,
    username: string
  ) =>
    _requestResetPassword({
      variables: {
        locationOrigin,
        tenant,
        username
      }
    });

  const [
    _requestLinkedAccountResetPassword,
    {
      loading: requestLinkedAccountResetPasswordLoading,
      error: requestLinkedAccountResetPasswordError,
      data: requestLinkedAccountResetPasswordData
    }
  ] = useLazyQuery<
    RequestLinkedAccountResetPasswordData,
    RequestLinkedAccountResetPasswordVars
  >(PublicGraphQL.Query.REQUEST_LINKED_ACCOUNT_RESET_PASSWORD, {
    client,
    fetchPolicy: 'network-only'
  });

  const requestLinkedAccountResetPassword = (
    tenant: string,
    user: UserUsername,
    parent: UserUsername
  ) =>
    _requestLinkedAccountResetPassword({
      variables: {
        request: {
          tenant,
          username: user.username,
          firstname: user.firstname,
          lastname: user.lastname,
          parentFirstname: parent.firstname,
          parentEmail: parent.email
        }
      }
    });

  return {
    canResetPassword,
    checkingUser,
    canResetPasswordData,
    canResetPasswordError,
    requestResetPassword,
    requestResetPasswordLoading,
    requestResetPasswordData,
    requestResetPasswordError,
    requestLinkedAccountResetPassword,
    requestLinkedAccountResetPasswordLoading,
    requestLinkedAccountResetPasswordData,
    requestLinkedAccountResetPasswordError
  };
};
